import React from 'react';
import { chakra, IconButton, Button } from '@chakra-ui/react';
import type { IconButtonProps, ButtonProps } from '@chakra-ui/react';

// =============================================================================
// OMNI AppHeader Button Components
// =============================================================================
// Universal button components using OMNI AppHeader recipes for consistent
// styling across all header buttons with proper OMNI design system compliance
// =============================================================================

// OMNI AppHeader Icon Button
// =============================================================================
// Used for brand home button and theme toggle with OMNI styling
export interface OmniAppHeaderIconButtonProps
  extends Omit<IconButtonProps, 'variant'> {
  variant?: 'default' | 'emphasized' | 'subtle' | 'contrast';
  size?: 'sm' | 'md' | 'lg';
}

// Create OMNI Brand Icon Button using factory pattern
const OmniBrandIconButtonBase = chakra(IconButton, {
  base: {
    bg: 'transparent',
    color: 'omni.text.primary',
    border: 'none',
    borderRadius: 'omni.radius.medium',
    p: { base: 1, sm: 1.5, md: 2 },
    minW: { base: '32px', sm: '36px', md: '40px' },
    h: { base: '32px', sm: '36px', md: '40px' },
    fontSize: { base: 'md', sm: 'lg', md: 'xl' },
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    _hover: {
      bg: 'omni.bg.hover.subtle',
      color: 'omni.text.primary',
      transform: 'scale(1.05)',
    },
    _focus: {
      bg: 'omni.bg.hover.subtle',
      boxShadow: '0 0 0 2px var(--omni-border-focus)',
      outline: 'none',
    },
    _active: {
      bg: 'omni.bg.active',
      transform: 'scale(0.98)',
    },
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      _hover: {
        bg: 'transparent',
        transform: 'none',
      },
    },
  },
  variants: {
    variant: {
      default: {},
      emphasized: {
        bg: 'omni.bg.primary',
        color: 'white',
        _hover: {
          bg: 'omni.bg.primary.hover',
        },
        _focus: {
          bg: 'omni.bg.primary.hover',
        },
      },
      subtle: {
        color: 'omni.text.secondary',
        _hover: {
          color: 'omni.text.primary',
        },
      },
      contrast: {
        // High contrast variant for AppHeader without background
        bg: 'rgba(255, 255, 255, 0.9)',
        color: 'gray.900',
        border: '1px solid',
        borderColor: 'gray.200',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        _hover: {
          bg: 'white',
          borderColor: 'brand.300',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
          transform: 'scale(1.05)',
        },
        _active: {
          bg: 'gray.50',
          borderColor: 'brand.500',
          transform: 'scale(0.98)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        _focus: {
          bg: 'white',
          borderColor: 'brand.500',
          outline: '2px solid',
          outlineColor: 'brand.200',
          outlineOffset: '2px',
        },
        _disabled: {
          bg: 'rgba(255, 255, 255, 0.6)',
          color: 'gray.400',
          borderColor: 'gray.100',
          boxShadow: 'none',
          _hover: {
            bg: 'rgba(255, 255, 255, 0.6)',
            transform: 'none',
          },
        },
      },
    },
    size: {
      sm: {
        minW: '28px',
        h: '28px',
        fontSize: 'sm',
        p: 1,
      },
      md: {
        minW: { base: '32px', sm: '36px', md: '40px' },
        h: { base: '32px', sm: '36px', md: '40px' },
        fontSize: { base: 'md', sm: 'lg', md: 'xl' },
      },
      lg: {
        minW: { base: '40px', sm: '44px', md: '48px' },
        h: { base: '40px', sm: '44px', md: '48px' },
        fontSize: { base: 'lg', sm: 'xl', md: '2xl' },
      },
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

export const OmniAppHeaderIconButton = React.forwardRef<
  HTMLButtonElement,
  OmniAppHeaderIconButtonProps
>((props, ref) => {
  const { variant = 'default', size = 'md', ...rest } = props;

  return (
    <OmniBrandIconButtonBase
      ref={ref}
      variant={variant}
      size={size}
      {...rest}
    />
  );
});

OmniAppHeaderIconButton.displayName = 'OmniAppHeaderIconButton';

// OMNI AppHeader Control Icon Button
// =============================================================================
// Used for theme toggle and other control icons with OMNI styling
export interface OmniAppHeaderControlIconButtonProps
  extends Omit<IconButtonProps, 'variant'> {
  variant?: 'default' | 'emphasized' | 'subtle' | 'contrast';
  size?: 'sm' | 'md' | 'lg';
}

// Create OMNI Control Icon Button using factory pattern
const OmniControlIconButtonBase = chakra(IconButton, {
  base: {
    bg: 'transparent',
    color: 'omni.text.primary',
    border: 'none',
    borderRadius: 'omni.radius.medium',
    p: { base: 1.5, sm: 2, md: 2.5 },
    minW: { base: '36px', sm: '40px', md: '44px' },
    h: { base: '36px', sm: '40px', md: '44px' },
    fontSize: { base: 'lg', sm: 'xl', md: '2xl' },
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    _hover: {
      bg: 'omni.bg.hover.subtle',
      color: 'omni.text.primary',
      transform: 'scale(1.05)',
    },
    _focus: {
      bg: 'omni.bg.hover.subtle',
      boxShadow: '0 0 0 2px var(--omni-border-focus)',
      outline: 'none',
    },
    _active: {
      bg: 'omni.bg.active',
      transform: 'scale(0.98)',
    },
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      _hover: {
        bg: 'transparent',
        transform: 'none',
      },
    },
  },
  variants: {
    variant: {
      default: {},
      emphasized: {
        bg: 'omni.bg.muted',
        _hover: {
          bg: 'omni.bg.hover.muted',
        },
      },
      subtle: {
        color: 'omni.text.secondary',
        _hover: {
          color: 'omni.text.primary',
          bg: 'omni.bg.hover.subtle',
        },
      },
      contrast: {
        // High contrast variant for icon buttons without background
        bg: 'rgba(255, 255, 255, 0.9)',
        color: 'gray.900',
        border: '1px solid',
        borderColor: 'gray.200',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        _hover: {
          bg: 'white',
          borderColor: 'brand.300',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
          transform: 'scale(1.05)',
        },
        _active: {
          bg: 'gray.50',
          borderColor: 'brand.500',
          transform: 'scale(0.98)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        _focus: {
          bg: 'white',
          borderColor: 'brand.500',
          outline: '2px solid',
          outlineColor: 'brand.200',
          outlineOffset: '2px',
        },
        _disabled: {
          bg: 'rgba(255, 255, 255, 0.6)',
          color: 'gray.400',
          borderColor: 'gray.100',
          boxShadow: 'none',
          _hover: {
            bg: 'rgba(255, 255, 255, 0.6)',
            transform: 'none',
          },
        },
      },
    },
    size: {
      sm: {
        minW: '32px',
        h: '32px',
        fontSize: 'md',
        p: 1.5,
      },
      md: {
        minW: { base: '36px', sm: '40px', md: '44px' },
        h: { base: '36px', sm: '40px', md: '44px' },
        fontSize: { base: 'lg', sm: 'xl', md: '2xl' },
      },
      lg: {
        minW: { base: '44px', sm: '48px', md: '52px' },
        h: { base: '44px', sm: '48px', md: '52px' },
        fontSize: { base: 'xl', sm: '2xl', md: '3xl' },
      },
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
});

export const OmniAppHeaderControlIconButton = React.forwardRef<
  HTMLButtonElement,
  OmniAppHeaderControlIconButtonProps
>((props, ref) => {
  const { variant = 'default', size = 'md', ...rest } = props;

  return (
    <OmniControlIconButtonBase
      ref={ref}
      variant={variant}
      size={size}
      {...rest}
    />
  );
});

OmniAppHeaderControlIconButton.displayName = 'OmniAppHeaderControlIconButton';

// OMNI AppHeader Control Button
// =============================================================================
// Used for login/logout buttons with OMNI styling
export interface OmniAppHeaderControlButtonProps
  extends Omit<ButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'contrast';
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

// Create OMNI Control Button using factory pattern
const OmniControlButtonBase = chakra(Button, {
  base: {
    bg: 'omni.bg.primary',
    color: 'white',
    border: 'none',
    borderRadius: 'omni.radius.medium',
    fontWeight: '500',
    letterSpacing: '0.025em',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    _hover: {
      bg: 'omni.bg.primary.hover',
      transform: 'translateY(-1px)',
      boxShadow: 'omni.shadow.medium',
    },
    _focus: {
      bg: 'omni.bg.primary.hover',
      boxShadow: '0 0 0 2px var(--omni-border-focus)',
      outline: 'none',
    },
    _active: {
      bg: 'omni.bg.primary.active',
      transform: 'translateY(0)',
    },
    _disabled: {
      bg: 'omni.bg.disabled',
      color: 'omni.text.disabled',
      cursor: 'not-allowed',
      opacity: 0.6,
      _hover: {
        bg: 'omni.bg.disabled',
        transform: 'none',
        boxShadow: 'none',
      },
    },
  },
  variants: {
    variant: {
      primary: {},
      secondary: {
        bg: 'omni.bg.secondary',
        color: 'omni.text.primary',
        border: '1px solid',
        borderColor: 'omni.border.subtle',
        _hover: {
          bg: 'omni.bg.secondary.hover',
          borderColor: 'omni.border.hover',
        },
        _focus: {
          bg: 'omni.bg.secondary.hover',
          borderColor: 'omni.border.focus',
        },
        _active: {
          bg: 'omni.bg.secondary.active',
        },
      },
      danger: {
        bg: 'omni.danger.500',
        color: 'white',
        _hover: {
          bg: 'omni.danger.600',
          boxShadow: 'omni.shadow.medium',
        },
        _focus: {
          bg: 'omni.danger.600',
          boxShadow: '0 0 0 2px var(--omni-danger-200)',
          outline: 'none',
        },
        _active: {
          bg: 'omni.danger.700',
          transform: 'translateY(0)',
        },
      },
      ghost: {
        bg: 'transparent',
        color: 'omni.text.primary',
        _hover: {
          bg: 'omni.bg.hover.subtle',
        },
        _focus: {
          bg: 'omni.bg.hover.subtle',
          boxShadow: '0 0 0 2px var(--omni-border-focus)',
          outline: 'none',
        },
        _active: {
          bg: 'omni.bg.active',
        },
      },
      contrast: {
        // High contrast variant for control buttons without background
        bg: 'rgba(255, 255, 255, 0.95)',
        color: 'gray.900',
        border: '1px solid',
        borderColor: 'gray.300',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        fontWeight: '600',
        _hover: {
          bg: 'white',
          borderColor: 'brand.400',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-1px)',
        },
        _focus: {
          bg: 'white',
          borderColor: 'brand.500',
          boxShadow: '0 0 0 2px rgba(0, 161, 210, 0.2)',
          outline: 'none',
        },
        _active: {
          bg: 'gray.50',
          borderColor: 'brand.600',
          transform: 'translateY(0)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        _disabled: {
          bg: 'rgba(255, 255, 255, 0.7)',
          color: 'gray.400',
          borderColor: 'gray.200',
          boxShadow: 'none',
        },
      },
    },
    size: {
      xs: {
        px: 2,
        py: 1,
        fontSize: '2xs',
        minW: '50px',
      },
      sm: {
        px: 3,
        py: 1.5,
        fontSize: 'xs',
        minW: { base: '60px', sm: '70px' },
      },
      md: {
        px: { base: 3, sm: 4, md: 6 },
        py: { base: 1.5, sm: 2, md: 2.5 },
        fontSize: { base: 'xs', sm: 'sm' },
        minW: { base: '60px', sm: '70px', md: '80px' },
      },
      lg: {
        px: { base: 4, sm: 6, md: 8 },
        py: { base: 2, sm: 3, md: 3.5 },
        fontSize: { base: 'sm', sm: 'md' },
        minW: { base: '80px', sm: '90px', md: '100px' },
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'sm',
  },
});

export const OmniAppHeaderControlButton = React.forwardRef<
  HTMLButtonElement,
  OmniAppHeaderControlButtonProps
>((props, ref) => {
  const { variant = 'primary', size = 'sm', ...rest } = props;

  return (
    <OmniControlButtonBase ref={ref} variant={variant} size={size} {...rest} />
  );
});

OmniAppHeaderControlButton.displayName = 'OmniAppHeaderControlButton';

OmniAppHeaderControlButton.displayName = 'OmniAppHeaderControlButton';

// =============================================================================
// EXPORTS
// =============================================================================
// All component types are already exported with their declarations above
